import React from "react";
import { Box } from "@mui/material";
import { CollapsibleBreadcrumb } from "../basic/CollapsibleBreadcrumb/CollapsibleBreadcrumb";
import { TopbarHeight } from "../../assets/global/Theme-variable";
import type { IBreadcrumbBar } from "./BreadcrumbBar.types";

/**
 * A reusable breadcrumb bar composed of:
 *  - `CollapsibleBreadcrumb` on the left (ancestor trail collapsed behind a
 *    "Back" control + the current page title),
 *  - an optional centered slot (`centerContent`),
 *  - an optional right-aligned slot (`rightContent`).
 *
 * Sticky positioning is opt-in via the `sticky` prop. When mounted the bar
 * measures its own height and reports it through `onHeightChange`, so callers
 * can anchor a sticky side panel directly beneath it.
 */
export const BreadcrumbBar: React.FC<IBreadcrumbBar> = ({
  navigation,
  title,
  centerContent,
  rightContent,
  onBack,
  backLabel,
  sticky = false,
  stickyTop = TopbarHeight,
  onHeightChange,
  sx,
}) => {
  const barRef = React.useRef<HTMLDivElement | null>(null);

  /* Measure the bar's border-box height and report it upward. offsetHeight is
     used (not contentRect) so the reported value includes vertical padding —
     the full occupied height a parent needs to offset a panel below the bar. */
  React.useEffect(() => {
    const el = barRef.current;
    if (!el || !onHeightChange || typeof ResizeObserver === "undefined") return;

    const measure = () => {
      if (el.offsetHeight) onHeightChange(el.offsetHeight);
    };
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [onHeightChange]);

  return (
    <Box
      ref={barRef}
      sx={{
        ...(sticky && {
          position: "sticky",
          top: `${stickyTop}px`,
          zIndex: 100,
        }),
        bgcolor: "background.paper",
        borderRadius: 1,
        mb: 1,
        py: 0,
        px: 0.75,
        display: "flex",
        /* On small screens stack the center content (e.g. tabs) onto its own
           row below the back-button/title row; keep everything inline from md up. */
        flexDirection: { xs: "column", md: "row" },
        alignItems: { xs: "stretch", md: "center" },
        gap: 1,
        ...sx,
      }}
    >
      {/* TOP ROW on mobile: LEFT (back + title) and RIGHT actions share the row.
         From md up this is just the LEFT zone with equal flex weight to RIGHT so
         the CENTER slot lands on the bar's true geometric center. */}
      <Box
        sx={{
          flex: { xs: "unset", md: 1 },
          minWidth: 0,
          display: "flex",
          alignItems: "center",
          gap: 1,
          width: { xs: "100%", md: "auto" },
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ minWidth: 0, display: "flex", justifyContent: "flex-start" }}>
          <CollapsibleBreadcrumb
            items={navigation}
            current={title}
            onBack={onBack}
            backLabel={backLabel}
          />
        </Box>

        {/* RIGHT actions ride alongside the title on mobile so the title row
            stays compact; on md+ they move to their own right-aligned zone. */}
        {rightContent && (
          <Box
            sx={{
              display: { xs: "flex", md: "none" },
              flexShrink: 0,
              alignItems: "center",
            }}
          >
            {rightContent}
          </Box>
        )}
      </Box>

      {/* CENTER — optional. Centered inline on md+ and centered on its own
          full-width row on mobile; stays horizontally scrollable so wide tabs
          are never clipped (content simply left-aligns when it overflows). */}
      {centerContent && (
        <Box
          sx={{
            flexShrink: 0,
            display: "flex",
            justifyContent: "center",
            width: { xs: "100%", md: "auto" },
            overflowX: { xs: "auto", md: "visible" },
            // Hide the scrollbar while keeping scroll ability on mobile.
            "&::-webkit-scrollbar": { display: "none" },
            scrollbarWidth: "none",
          }}
        >
          {centerContent}
        </Box>
      )}

      {/* RIGHT — md+ only (on mobile it renders next to the title above). */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: { xs: "none", md: "flex" },
          justifyContent: "flex-end",
          alignItems: "center",
        }}
      >
        {rightContent}
      </Box>
    </Box>
  );
};
